import React, { useState } from "react";
import { COLORS } from "../../constants/colors.js";
import { FONTS } from "../../constants/fonts.js";
import {
  DocumentTextIcon,
  TagIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

const styles = {
  container: {
    marginBottom: 24,
    width: "100%",
  },
  label: {
    fontSize: 16,
    color: COLORS.text,
    marginBottom: 8,
    fontFamily: FONTS.regularInter,
    fontWeight: 500,
  },
  inputContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.white,
    borderRadius: 12,
    border: `1px solid ${COLORS.gray}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    overflow: "hidden",
    "&:hover": {
      borderColor: COLORS.primary,
      boxShadow: `0 4px 12px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  input: {
    flex: 1,
    height: 56,
    fontSize: 16,
    color: COLORS.text,
    border: "none",
    padding: "0 12px",
    fontFamily: FONTS.regularInter,
    outline: "none",
    width: "100%",
    background: "transparent",
  },
  textarea: {
    flex: 1,
    minHeight: 120,
    fontSize: 16,
    color: COLORS.text,
    border: "none",
    padding: 12,
    fontFamily: FONTS.regularInter,
    outline: "none",
    width: "100%",
    resize: "vertical",
    background: "transparent",
  },
  iconContainer: {
    padding: 12,
    display: "flex",
    alignItems: "center",
    color: COLORS.gray,
  },
  separator: {
    color: COLORS.gray,
    fontSize: 20,
    lineHeight: "56px",
    marginRight: 8,
  },
  eyeIconContainer: {
    padding: 12,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    color: COLORS.gray,
    "&:hover": {
      color: COLORS.primary,
    },
  },
  error: {
    fontSize: 14,
    color: COLORS.error,
    marginTop: 4,
    fontFamily: FONTS.regularInter,
  },
};

const CustomInput = ({
  label,
  value,
  onChangeText,
  icon,
  placeholder,
  type = "text",
  multiline = false,
  secureTextEntry = false,
  error = "",
  style = {},
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const getIcon = () => {
    switch (icon) {
      case "description":
        return <DocumentTextIcon width={20} height={20} stroke={COLORS.gray} />;
      case "label":
        return <TagIcon width={20} height={20} stroke={COLORS.gray} />;
      case "attach-money":
      case "cash":
        return (
          <CurrencyDollarIcon width={20} height={20} stroke={COLORS.gray} />
        );
      case "mail":
        return <EnvelopeIcon width={20} height={20} stroke={COLORS.gray} />;
      case "lock":
        return <LockClosedIcon width={20} height={20} stroke={COLORS.gray} />;
      default:
        return null;
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div style={styles.container}>
      {label && <span style={styles.label}>{label}</span>}
      <div style={styles.inputContainer}>
        {icon && (
          <>
            <div style={styles.iconContainer}>{getIcon()}</div>
            <div style={styles.separator}>|</div>
          </>
        )}
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChangeText(e.target.value)}
            placeholder={placeholder}
            style={{ ...styles.textarea, ...style }}
          />
        ) : (
          <input
            type={secureTextEntry && !showPassword ? "password" : type}
            value={value}
            onChange={(e) => onChangeText(e.target.value)}
            placeholder={placeholder}
            style={styles.input}
          />
        )}
        {secureTextEntry && (
          <div
            style={styles.eyeIconContainer}
            onClick={togglePasswordVisibility}
          >
            {showPassword ? (
              <EyeSlashIcon width={20} height={20} stroke={COLORS.gray} />
            ) : (
              <EyeIcon width={20} height={20} stroke={COLORS.gray} />
            )}
          </div>
        )}
      </div>
      {error && <span style={styles.error}>{error}</span>}
    </div>
  );
};

export default CustomInput;
